import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ReactElement, ReactNode, RefObject } from "react";
import { Button, type ButtonProps } from "../Button/Button";
import { IconButton } from "../IconButton/IconButton";
import styles from "./Dialog.module.css";

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Names the task ("Receive lots"). Required: it labels the dialog for assistive technology. */
  title: ReactNode;
  description?: ReactNode;
  /** Optional element that opens the dialog (a Button); omit when controlling `open` yourself. */
  trigger?: ReactElement;
  /** Buttons row: a Cancel (DialogClose) and the primary action naming the result ("Receive 3 lots"). */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Element to focus on open. By default the first focusable element, except on touch devices. */
  initialFocus?: RefObject<HTMLElement | null>;
  children?: ReactNode;
}

/** A focused sub-task in a modal (Base UI Dialog): focus trap, Escape and focus return built in. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  trigger,
  footer,
  size = "md",
  initialFocus,
  children,
}: DialogProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}>
      {trigger ? <BaseDialog.Trigger render={trigger} /> : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={styles.backdrop} />
        <BaseDialog.Viewport className={styles.viewport}>
          <BaseDialog.Popup
            className={styles.popup}
            data-size={size}
            initialFocus={initialFocus ?? ((openType) => openType !== "touch")}
          >
            <header className={styles.header}>
              <div className={styles.heading}>
                <BaseDialog.Title className={styles.title}>{title}</BaseDialog.Title>
                {description ? (
                  <BaseDialog.Description className={styles.description}>{description}</BaseDialog.Description>
                ) : null}
              </div>
              <BaseDialog.Close render={<IconButton icon="x" label="Close" tooltip={false} size="sm" />} />
            </header>
            {children ? <div className={styles.body}>{children}</div> : null}
            {footer ? <footer className={styles.footer}>{footer}</footer> : null}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/** A button that closes the surrounding Dialog ("Cancel"). */
export function DialogClose({ children = "Cancel", variant = "secondary", ...rest }: Omit<ButtonProps, "onClick">) {
  return <BaseDialog.Close render={<Button variant={variant} {...rest} />}>{children}</BaseDialog.Close>;
}
